// should-grid-handle-tab-commit.ts — PSTUDIO-1433

export interface GridTabCommitState {
  isCellBeingEdited: boolean;
  /** `document.activeElement` at the moment Tab was pressed. */
  activeElement: Element | null;
}

/**
 * PSTUDIO-1433: should the grid's capture-phase Tab handler claim this Tab
 * (commit the cell + move focus horizontally)?
 *
 * It must claim it only when the edit it would commit actually lives in a grid
 * cell. `isCellBeingEdited` alone is not that signal — focus in the Expanded
 * Cell View flips it too — so the grid used to capture Tab there
 * (`preventDefault()` + `stopPropagation()`) and then do nothing, because the
 * handler's own `activeEl.closest('td')` was null. The key never reached
 * CodeMirror, which is why the product's "Tab = inline editing indent" could
 * not work at all (measured live 29 Aug: focus stayed put, document unchanged).
 *
 * The signal is therefore WHERE THE FOCUS IS, asked exactly as the handler
 * itself asks it one line later. Gating on `editingRowId`/`editingColumnId`
 * instead looks equivalent but is not: those are set only by `toggleCellInput`
 * for the inline text/number editor. A behaviors multi-select, an asset-type
 * select and a checkbox all set `isCellBeingEdited` WITHOUT them
 * (`table-store.ts` — the `behaviors` branch and the `enter` hotkey), so an
 * id-based gate handed their Tab to the browser while `isCellBeingEdited`
 * stayed `true` forever: no commit, no blur, and every grid shortcut gated on
 * that flag (copy/paste, navigation, undo — 20+ call sites) dead until the user
 * clicked elsewhere. Focus position covers all of them, because those controls
 * live inside the `<td>` (their shadow roots retarget `document.activeElement`
 * to the host, which is in the cell's light DOM), while the panel's editor
 * does not.
 */
export function shouldGridHandleTabCommit({
  isCellBeingEdited,
  activeElement,
}: GridTabCommitState): boolean {
  if (!isCellBeingEdited) return false;
  return !!activeElement?.closest('td');
}
